import {
  ApFlagId,
  flowStructureUtil,
  isNil,
  PieceAction,
  PieceActionSettings,
  PieceTrigger,
  PieceTriggerSettings,
} from '@activepieces/shared';
import { t } from 'i18next';
import { Sparkle } from 'lucide-react';
import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { useBuilderStateContext } from '../../builder-hooks';
import { AutoPropertiesFormComponent } from '../../piece-properties/auto-properties-form';
import { useStepSettingsContext } from '../step-settings-context';

import { ConnectionSelect } from './connection-select';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { flagsHooks } from '@/hooks/flags-hooks';

type PieceSettingsProps = {
  step: PieceAction | PieceTrigger;
  flowId: string;
  readonly: boolean;
  tenantId: string;
  fieldsGeneration: number;
};

const removeAuthFromProps = (
  props: Record<string, any>,
): Record<string, any> => {
  const { auth, ...rest } = props;
  return rest;
};

const PieceSettings = React.memo((props: PieceSettingsProps) => {
  const { pieceModel } = useStepSettingsContext();
  const refreshToggle = useBuilderStateContext(
    (state) => state.refreshStepFormSettingsToggle,
  );
  const selectedStep = useBuilderStateContext((state) => state.selectedStep);
  const flowVersion = useBuilderStateContext((state) => state.flowVersion);
  const highlightKey = useBuilderStateContext((state) => state.highlightKey);
  const requestAiHelp = useBuilderStateContext((state) => state.requestAiHelp);

  const form = useFormContext();
  useEffect(() => {
    if (selectedStep && flowVersion) {
      const updatedStep = flowStructureUtil.getStep(selectedStep, flowVersion);
      if (updatedStep?.settings.input) {
        const resetData = {
          name: updatedStep.name,
          displayName: updatedStep.displayName,
          settings: {
            ...updatedStep.settings,
          },
          type: updatedStep.type,
        };
        form.reset(resetData);
      }
    }
  }, [refreshToggle]);
  const actionName = (props.step.settings as PieceActionSettings).actionName;
  const selectedAction = actionName
    ? pieceModel?.actions[actionName]
    : undefined;
  const triggerName = (props.step.settings as PieceTriggerSettings).triggerName;
  const selectedTrigger = triggerName
    ? pieceModel?.triggers[triggerName]
    : undefined;

  const actionPropsWithoutAuth = removeAuthFromProps(
    selectedAction?.props ?? {},
  );
  const triggerPropsWithoutAuth = removeAuthFromProps(
    selectedTrigger?.props ?? {},
  );

  const { data: webhookPrefixUrl } = flagsHooks.useFlag<string>(
    ApFlagId.WEBHOOK_URL_PREFIX,
  );

  const { data: pausedFlowTimeoutDays } = flagsHooks.useFlag<number>(
    ApFlagId.PAUSED_FLOW_TIMEOUT_DAYS,
  );

  const { data: webhookTimeoutSeconds } = flagsHooks.useFlag<number>(
    ApFlagId.WEBHOOK_TIMEOUT_SECONDS,
  );

  const { data: frontendUrl } = flagsHooks.useFlag<string>(ApFlagId.PUBLIC_URL);

  const markdownVariables = {
    webhookUrl: `${webhookPrefixUrl}/${props.tenantId}/${props.flowId}`,
    formUrl: `${frontendUrl}forms/${props.tenantId}/${props.flowId}`,
    chatUrl: `${frontendUrl}chats/${props.tenantId}/${props.flowId}`,
    pausedFlowTimeoutDays: pausedFlowTimeoutDays?.toString() ?? '',
    webhookTimeoutSeconds: webhookTimeoutSeconds?.toString() ?? '',
  };

  const showAuthForAction =
    !isNil(selectedAction) && (selectedAction.requireAuth ?? true);
  const showAuthForTrigger =
    !isNil(selectedTrigger) && (selectedTrigger.requireAuth ?? true);
  return (
    <div className="flex flex-col gap-4 w-full">
      {!pieceModel && (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="w-full h-8" />
          ))}
        </div>
      )}
      <div className="flex flex-col gap-2 items-center">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="sm"
              variant="basic"
              className="w-auto"
              onClick={() => requestAiHelp(selectedStep ?? '')}
            >
              <div className="flex gap-2 items-center">
                <Sparkle className="w-4 h-4" />
                {t('Need Help ?')}
              </div>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">{t('Open AI Builder')}</TooltipContent>
        </Tooltip>
      </div>

      {pieceModel && (
        <>
          {pieceModel.auth && (showAuthForAction || showAuthForTrigger) && (
            <ConnectionSelect
              isTrigger={!isNil(selectedTrigger)}
              piece={pieceModel}
              disabled={props.readonly}
            ></ConnectionSelect>
          )}
          {selectedAction && (
            <AutoPropertiesFormComponent
              key={`${selectedAction.name}-${refreshToggle}-${props.fieldsGeneration}`}
              prefixValue="settings.input"
              props={actionPropsWithoutAuth}
              allowDynamicValues={true}
              disabled={props.readonly}
              useMentionTextInput={true}
              markdownVariables={markdownVariables}
              highlightKey={highlightKey ?? undefined}
            />
          )}
          {selectedTrigger && (
            <AutoPropertiesFormComponent
              key={`${selectedTrigger.name}-${refreshToggle}-${props.fieldsGeneration}`}
              prefixValue={'settings.input'}
              props={triggerPropsWithoutAuth}
              useMentionTextInput={true}
              allowDynamicValues={true}
              disabled={props.readonly}
              markdownVariables={markdownVariables}
              highlightKey={highlightKey ?? undefined}
            ></AutoPropertiesFormComponent>
          )}
        </>
      )}
    </div>
  );
});

PieceSettings.displayName = 'PieceSettings';
export { PieceSettings };
